import React, { FunctionComponent } from 'react';
import { DealerLaunchStepProps } from '../types';
import { Dealer } from '../../../types/dealerTypes';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';
import { TextField } from '../../Input/TextField/TextField';
import {
  bankAccountNumberValidation,
  bankRoutingNumberValidation,
  emailValidatorFunction,
} from '../../../utils/validation';
import { submitLendingData } from '../../../utils/lending';
import { useTypedSelector } from '../../../redux/store';

const title = 'How can we pay you?';
const subTitle =
  'Your payment information is essential so that we know where to send your payments.';
const cardTitle = 'Payment Information';
const cardDescription =
  'Enter your reserve email address and bank information.';
const cardSubFooter =
  'While every reasonable effort is made to ensure the accuracy of this data, we are not responsible for any errors or omissions contained on these pages.';

export const DealerLaunchLendingPayment: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
  saveDealer,
}) => {
  const form = useLaunchpadForm<Dealer>({ defaultValues: dealer });
  const paymentInfo = dealer?.lending?.paymentInfo;
  const isAdminAccess = !!sessionStorage.getItem('adminAccessDealer');
  const globalConfigurationStore = useTypedSelector(
    (state) => state.globalConfigurations,
  );
  const { globalConfigurations } = globalConfigurationStore;

  // For bankName, accountName, we mask them if they have data in Admin view. For routingNumber, accountNumber we mask them regardless.
  const inputTypeGenerator = (field: keyof NonNullable<typeof paymentInfo>) => {
    return (isAdminAccess &&
      (field === 'bankName' || field === 'accountName') &&
      paymentInfo &&
      paymentInfo[field]) ||
      field === 'routingNumber' ||
      field === 'accountNumber'
      ? 'password'
      : 'text';
  };

  const bankNameInputType = inputTypeGenerator('bankName');
  const accountNameInputType = inputTypeGenerator('accountName');
  const routingInputType = inputTypeGenerator('routingNumber');
  const accountInputType = inputTypeGenerator('accountNumber');

  // in admin view, it is not editable (readOnly) if there is a value for bankName, accountName, routingNumber, or accountNumber.
  const shouldReadOnly = (
    inputType: string,
    field: keyof NonNullable<typeof paymentInfo>,
  ) => {
    return (
      inputType === 'password' &&
      !!isAdminAccess &&
      !!paymentInfo &&
      !!paymentInfo[field] &&
      (field === 'bankName' ||
        field === 'accountName' ||
        field === 'routingNumber' ||
        field === 'accountNumber')
    );
  };

  const onSubmit = async (data: Dealer) => {
    // update dealer first with payment info
    const lendingPayment = dealer?.lending
      ? {
          ...dealer.lending,
          paymentInfo: data.lending?.paymentInfo,
        }
      : undefined;
    const dealerLendingPaymentInfo: Partial<Dealer> = {
      lending: lendingPayment,
    };
    await saveDealer(dealerLendingPaymentInfo);

    await submitLendingData(dealer, globalConfigurations);

    return nextStep();
  };

  return (
    <DealerLaunchFormCardLayout
      form={form}
      title={title}
      subTitle={subTitle}
      cardTitle={cardTitle}
      cardSubTitle={cardDescription}
      prevStep={prevStep}
      onFormSubmit={onSubmit}
      showSecure
      subFooterText={cardSubFooter}
    >
      <div className={styles.stepRow}>
        <TextField
          label="Dealer Reserve Email Address"
          tooltip="Your email address where you want to receive notifications about reserve payments."
          name="lending.paymentInfo.email"
          width="md"
          form={form}
          validation={{ required: true, validate: emailValidatorFunction }}
        />
      </div>
      <div className={styles.stepRow}>
        <TextField
          label="Bank Name"
          tooltip="Your bank name (e.g. Bank of America)"
          name="lending.paymentInfo.bankName"
          width="md"
          form={form}
          validation={{ required: true }}
          type={bankNameInputType}
          readOnly={shouldReadOnly(bankNameInputType, 'bankName')}
        />
        <TextField
          label="Account Name"
          tooltip="Your bank account name to receive reserve payments."
          name="lending.paymentInfo.accountName"
          width="md"
          form={form}
          validation={{ required: true }}
          type={accountNameInputType}
          readOnly={shouldReadOnly(accountNameInputType, 'accountName')}
        />
      </div>
      <div className={styles.stepRow}>
        <TextField
          label="Routing Number"
          tooltip="Your 9-digit bank routing number."
          name="lending.paymentInfo.routingNumber"
          width="md"
          form={form}
          type={routingInputType}
          validation={{ required: true, validate: bankRoutingNumberValidation }}
          readOnly={shouldReadOnly(routingInputType, 'routingNumber')}
        />
        <TextField
          label="Account Number"
          tooltip="Your bank account number where you want to receive payments."
          name="lending.paymentInfo.accountNumber"
          width="md"
          form={form}
          type={accountInputType}
          validation={{ required: true, validate: bankAccountNumberValidation }}
          readOnly={shouldReadOnly(accountInputType, 'bankName')}
        />
      </div>
    </DealerLaunchFormCardLayout>
  );
};
